export interface Point2D {
  x: number;
  y: number;
}

export interface CubicBezierCurve {
  p0: Point2D;
  p1: Point2D;
  p2: Point2D;
  p3: Point2D;
}

export function evaluateCubicBezier(t: number, p0: Point2D, p1: Point2D, p2: Point2D, p3: Point2D): Point2D {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  const t2 = t * t;
  const t3 = t2 * t;

  return {
    x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
    y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y
  };
}

/**
 * Coons patch interpolation from 4 boundary cubic Bezier curves.
 * Curves are: top (u-direction, v=0), bottom (u-direction, v=1),
 * left (v-direction, u=0), right (v-direction, u=1).
 *
 * Corners: top.p0 = left.p0, top.p3 = right.p0, bottom.p0 = left.p3, bottom.p3 = right.p3
 */
export function evaluateCoonsPatch(
  u: number,
  v: number,
  top: CubicBezierCurve,
  bottom: CubicBezierCurve,
  left: CubicBezierCurve,
  right: CubicBezierCurve
): Point2D {
  const topPt = evaluateCubicBezier(u, top.p0, top.p1, top.p2, top.p3);
  const bottomPt = evaluateCubicBezier(u, bottom.p0, bottom.p1, bottom.p2, bottom.p3);
  const leftPt = evaluateCubicBezier(v, left.p0, left.p1, left.p2, left.p3);
  const rightPt = evaluateCubicBezier(v, right.p0, right.p1, right.p2, right.p3);

  const c00 = top.p0;
  const c10 = top.p3;
  const c01 = bottom.p0;
  const c11 = bottom.p3;

  // Bilinear interpolation of ruled surfaces minus bilinear corner correction
  const lerpU: Point2D = {
    x: (1 - v) * topPt.x + v * bottomPt.x,
    y: (1 - v) * topPt.y + v * bottomPt.y
  };

  const lerpV: Point2D = {
    x: (1 - u) * leftPt.x + u * rightPt.x,
    y: (1 - u) * leftPt.y + u * rightPt.y
  };

  const bilinear: Point2D = {
    x: (1 - u) * (1 - v) * c00.x + u * (1 - v) * c10.x + (1 - u) * v * c01.x + u * v * c11.x,
    y: (1 - u) * (1 - v) * c00.y + u * (1 - v) * c10.y + (1 - u) * v * c01.y + u * v * c11.y
  };

  return {
    x: lerpU.x + lerpV.x - bilinear.x,
    y: lerpU.y + lerpV.y - bilinear.y
  };
}

function cross2D(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}

export function pointInTriangle(px: number, py: number, ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
  const d1 = cross2D(bx - ax, by - ay, px - ax, py - ay);
  const d2 = cross2D(cx - bx, cy - by, px - bx, py - by);
  const d3 = cross2D(ax - cx, ay - cy, px - cx, py - cy);

  const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0);
  const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0);

  return !(hasNeg && hasPos);
}

export function pointInQuad(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
  dx: number, dy: number
): boolean {
  return pointInTriangle(px, py, ax, ay, bx, by, cx, cy) ||
         pointInTriangle(px, py, ax, ay, cx, cy, dx, dy);
}
